import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useDataProvider, useRefresh } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import { Dialog } from 'components/common';
import { formatCentsPrice } from 'services/helpers/formatPrice';

import RequestBankVerificationDialog from './RequestBankVerificationDialog';
import {
  AchAllocation,
  AchTransactionRecord,
  AchTransactionsCheckResponse,
  BankTransaction,
  VERDICT_NOT_FOUND,
  VERDICT_NO_TRANSACTIONS_ACCESS,
  VERDICT_PENDING,
  VERDICT_POSTED,
  VERDICT_REFRESH_PENDING,
} from './achVerificationTypes';
import {
  apiErrorMessage,
  formatDate,
  formatDateTime,
  parseUsdToCents,
  shortIntent,
  verdictLabel,
} from './achVerificationHelpers';

const GREEN = '#2e7d32';
const GREEN_BG = '#edf7ed';
const AMBER = '#b26a00';
const AMBER_BG = '#fff4e5';

const useStyles = makeStyles((theme) => ({
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    minHeight: 24,
  },

  label: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    whiteSpace: 'nowrap',
  },

  value: {
    fontSize: 13,
    textAlign: 'right',
  },

  valueStrong: {
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'right',
  },

  divider: {
    margin: theme.spacing(1.5, 0),
  },

  chipPosted: {
    backgroundColor: GREEN,
    color: '#fff',
    height: 20,
  },

  chipPending: {
    backgroundColor: AMBER_BG,
    color: AMBER,
    height: 20,
  },

  chipMuted: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    height: 20,
  },

  chipError: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    height: 20,
  },

  verdict: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: '1px solid transparent',
  },

  verdictOk: {
    backgroundColor: GREEN_BG,
    borderColor: GREEN,
    color: GREEN,
  },

  verdictWarn: {
    backgroundColor: AMBER_BG,
    borderColor: AMBER,
    color: AMBER,
  },

  verdictUnknown: {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.secondary,
  },

  verdictTitle: {
    fontSize: 13,
    fontWeight: 600,
  },

  verdictText: {
    fontSize: 12,
  },

  verdictAction: {
    marginTop: theme.spacing(1),
  },

  tableTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    fontSize: 13,
    fontWeight: 600,
  },

  matchingAmount: {
    fontWeight: 600,
  },

  cell: {
    fontSize: 12,
    padding: theme.spacing(0.5, 1),
  },

  matchRow: {
    backgroundColor: GREEN_BG,
  },

  checkedAt: {
    marginTop: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  previousResult: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: AMBER_BG,
    color: AMBER,
    fontSize: 13,
    fontWeight: 600,
  },

  override: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    border: `1px dashed ${AMBER}`,
    borderRadius: 4,
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  // rollo user id (uuid), not the billing id
  userId: string;
  // transaction group record of the pending ACH deposit
  record: AchTransactionRecord;
}

const formatDebit = (amountCents?: number | null) =>
  amountCents === null || amountCents === undefined
    ? 'n/a'
    : `${amountCents < 0 ? '-' : '+'}${formatCentsPrice(Math.abs(amountCents))}`;

/**
 * RLPR-20420 admin check of a pending ACH deposit against the bank-side transactions Stripe Financial Connections
 * exposes: a posted debit for the deposit amount is the evidence to release the pending funds early.
 */
const AchTransactionsCheckDialog = ({ open, onClose, userId, record }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<AchTransactionsCheckResponse | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  // test mode only: usd text of the debit amount to match instead of the deposit amount (sandbox banks return
  // synthetic transactions that never carry it); read through a ref so "Check again" sends the latest value
  const [overrideText, setOverrideText] = useState<string>('');
  const overrideRef = useRef('');
  const mountedRef = useRef(false);
  const dialogOpenRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const activeRequestRef = useRef<number | null>(null);
  // whether a check completed while open, so closing refreshes the datagrid for the release dialog
  const checkedRef = useRef(false);
  // NO_TRANSACTIONS_ACCESS: the relink request dialog is opened from here, no need to hunt for the menu item
  const [requestOpen, setRequestOpen] = useState<boolean>(false);
  const [requested, setRequested] = useState<boolean>(false);

  const paymentIntent: string | undefined = record?.details?.paymentIntentId;
  const allocation: AchAllocation | undefined = record?.details?.achAllocation;

  const runCheck = useCallback(async () => {
    if (!dialogOpenRef.current || activeRequestRef.current !== null) {
      return;
    }
    if (typeof userId !== 'string' || !userId.trim() || !paymentIntent) {
      setError('Unable to check: missing deposit information');
      return;
    }

    const overrideCents = parseUsdToCents(overrideRef.current);
    if (overrideRef.current.trim() && overrideCents === null) {
      setError('Enter a positive USD match amount with at most two decimal places');
      return;
    }

    const requestId = ++requestSequenceRef.current;
    activeRequestRef.current = requestId;
    const isCurrentRequest = () => mountedRef.current && requestSequenceRef.current === requestId;

    try {
      setLoading(true);
      setError(undefined);

      const { data } = await dataProvider.post('checkAchTransactions', {
        userId,
        data: {
          paymentIntent,
          amountCentsOverride: overrideCents === null ? undefined : overrideCents,
        },
      });

      if (isCurrentRequest()) {
        checkedRef.current = true;
        if (dialogOpenRef.current) {
          setResult(data);
        }
      } else {
        // The backend still saved the check even if its row was removed or changed deposits.
        refresh();
      }
    } catch (e) {
      if (isCurrentRequest() && dialogOpenRef.current) {
        setError(apiErrorMessage(e, 'Bank transactions check failed'));
      }
    } finally {
      if (isCurrentRequest()) {
        activeRequestRef.current = null;
        if (dialogOpenRef.current) {
          setLoading(false);
        } else if (checkedRef.current) {
          checkedRef.current = false;
          refresh();
        }
      }
    }
  }, [dataProvider, userId, paymentIntent, refresh]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    requestSequenceRef.current += 1;
    activeRequestRef.current = null;
    checkedRef.current = false;
    setLoading(false);
    setError(undefined);
    setResult(null);
    setOverrideText('');
    overrideRef.current = '';
    setRequestOpen(false);
    setRequested(false);

    return () => {
      mountedRef.current = false;
      dialogOpenRef.current = false;
      requestSequenceRef.current += 1;
      activeRequestRef.current = null;
      if (checkedRef.current) {
        checkedRef.current = false;
        refresh();
      }
    };
  }, [userId, paymentIntent, refresh]);

  useLayoutEffect(() => {
    dialogOpenRef.current = open;
    if (!open) {
      if (activeRequestRef.current === null && checkedRef.current) {
        checkedRef.current = false;
        refresh();
      }
      return;
    }

    setError(undefined);
    if (activeRequestRef.current !== null) {
      setLoading(true);
      return;
    }

    setResult(null);
    setOverrideText('');
    overrideRef.current = '';
    setRequested(false);
    runCheck();
  }, [open, runCheck, refresh]);

  const handleClose = () => {
    dialogOpenRef.current = false;
    setRequestOpen(false);
    if (activeRequestRef.current === null && checkedRef.current) {
      checkedRef.current = false;
      // reloads the deposit row so the release dialog sees details.bankVerification
      refresh();
    }
    onClose();
  };

  const statusChip = (status?: string | null) => (
    <Chip
      size="small"
      label={status || 'unknown'}
      className={
        status === 'posted'
          ? classes.chipPosted
          : status === 'pending'
            ? classes.chipPending
            : status === 'void'
              ? classes.chipError
              : classes.chipMuted
      }
    />
  );

  const renderRow = (label: string, value: React.ReactNode, strong = false) => (
    <Box key={label} className={classes.row}>
      <Typography className={classes.label}>{label}</Typography>
      {typeof value === 'string' ? (
        <Typography className={strong ? classes.valueStrong : classes.value}>{value}</Typography>
      ) : (
        value
      )}
    </Box>
  );

  const renderVerdict = (r: AchTransactionsCheckResponse) => {
    const match = r.bestMatch;
    if (r.verdict === VERDICT_POSTED && match) {
      return (
        <Box
          className={`${classes.verdict} ${r.testMode ? classes.verdictUnknown : classes.verdictOk}`}>
          {r.testMode ? <InfoOutlined fontSize="small" /> : <CheckCircleOutline fontSize="small" />}
          <div>
            <Typography className={classes.verdictTitle}>{verdictLabel(r.verdict)}</Typography>
            <Typography className={classes.verdictText}>
              {r.testMode ? (
                <>
                  Synthetic debit {formatDebit(match.amountCents)} matched a posted sandbox
                  transaction. This is not evidence that real money left the user&apos;s account.
                </>
              ) : (
                <>
                  {formatDebit(match.amountCents)} posted on {formatDate(match.postedAt)}. The money
                  left the user&apos;s account, releasing the pending{' '}
                  {formatCentsPrice(r.pendingAmountCents)} is low risk.
                </>
              )}
            </Typography>
          </div>
        </Box>
      );
    }
    if (r.verdict === VERDICT_PENDING && match) {
      return (
        <Box className={`${classes.verdict} ${classes.verdictWarn}`}>
          <InfoOutlined fontSize="small" />
          <div>
            <Typography className={classes.verdictTitle}>{verdictLabel(r.verdict)}</Typography>
            <Typography className={classes.verdictText}>
              The bank shows the {formatDebit(match.amountCents)} debit but it has not posted yet.
              Check again later or judge with the balance.
            </Typography>
          </div>
        </Box>
      );
    }
    return (
      <Box className={`${classes.verdict} ${classes.verdictUnknown}`}>
        <InfoOutlined fontSize="small" />
        <div>
          <Typography className={classes.verdictTitle}>{verdictLabel(r.verdict)}</Typography>
          <Typography className={classes.verdictText}>
            {r.verdict === VERDICT_NO_TRANSACTIONS_ACCESS
              ? requested
                ? 'Bank verification requested. Once the user relinked the bank account, check again.'
                : 'The user has to relink the bank account with transactions access before the debit can be looked up. Request the verification, then check again once the user is done.'
              : r.verdict === VERDICT_REFRESH_PENDING
                ? 'Stripe is still pulling the transactions from the bank. Check again in a few seconds.'
                : r.verdict === VERDICT_NOT_FOUND
                  ? 'No debit of the deposit amount appeared in the transactions Stripe returned. The bank may not show it yet.'
                  : ''}
          </Typography>
          {r.verdict === VERDICT_NO_TRANSACTIONS_ACCESS && (
            <Button
              className={classes.verdictAction}
              variant="outlined"
              size="small"
              color="primary"
              disabled={loading}
              onClick={() => setRequestOpen(true)}>
              {requested ? 'Send the request again' : 'Request bank verification'}
            </Button>
          )}
        </div>
      </Box>
    );
  };

  const renderCandidates = (candidates: BankTransaction[], bestMatchId?: string | null) => {
    if (!candidates.length) {
      return null;
    }
    return (
      <>
        <Typography className={classes.tableTitle}>
          Recent debits on the account (matching amounts first)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Date</TableCell>
              <TableCell className={classes.cell}>Description</TableCell>
              <TableCell className={classes.cell} align="right">
                Amount
              </TableCell>
              <TableCell className={classes.cell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.map((t) => (
              <TableRow key={t.id} className={t.id === bestMatchId ? classes.matchRow : undefined}>
                <TableCell className={classes.cell}>{formatDate(t.transactedAt)}</TableCell>
                <TableCell className={classes.cell}>{t.description || '-'}</TableCell>
                <TableCell
                  className={`${classes.cell} ${t.amountMatches ? classes.matchingAmount : ''}`}
                  align="right">
                  {formatDebit(t.amountCents)}
                </TableCell>
                <TableCell className={classes.cell}>{statusChip(t.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    );
  };

  const renderOverride = (r: AchTransactionsCheckResponse) =>
    r.testMode ? (
      <Box className={classes.override}>
        <TextField
          label="Match amount override (USD, Stripe test mode)"
          value={overrideText}
          onChange={(event) => {
            overrideRef.current = event.target.value;
            setOverrideText(event.target.value);
          }}
          placeholder="e.g. 10.00"
          fullWidth
          size="small"
          margin="dense"
          disabled={loading}
          helperText='Sandbox banks return synthetic debits that never carry the deposit amount. Type the amount of one of the debits below and "Check again" to exercise the posted path. Billing refuses this against the live Stripe account.'
        />
      </Box>
    ) : null;

  const renderResult = (r: AchTransactionsCheckResponse) => (
    <>
      {r.testMode && (
        <Typography className={classes.previousResult}>
          Stripe sandbox: these bank transactions are simulated and do not confirm a real debit.
        </Typography>
      )}
      {(loading || error) && (
        <Typography className={classes.previousResult} role="status">
          {loading
            ? 'Checking again. Showing previous result.'
            : 'Latest check failed. Showing previous result.'}
        </Typography>
      )}
      <Box className={classes.rows}>
        {renderRow('Deposit', shortIntent(r.paymentIntent))}
        {renderRow('ACH funding (debit)', formatCentsPrice(r.depositAmountCents), true)}
        {r.matchedAmountCents !== r.depositAmountCents &&
          renderRow(
            'Matched against (test override)',
            <Chip
              size="small"
              label={formatCentsPrice(r.matchedAmountCents)}
              className={classes.chipPending}
            />
          )}
        {renderRow('Stripe status', r.paymentIntentStatus || '-')}
        {renderRow('Still pending', formatCentsPrice(r.pendingAmountCents))}
        {renderRow('Bank account', `${r.bankName || 'Bank'} •••• ${r.lastDigits || '????'}`)}
        {r.financialConnectionsAccount &&
          renderRow('Financial Connections', r.financialConnectionsAccount)}
        {r.financialConnectionsAccount &&
          renderRow(
            'Transactions refresh',
            <Chip
              size="small"
              label={
                r.refreshStatus === 'pending'
                  ? 'Refresh pending'
                  : r.refreshStatus === 'failed'
                    ? 'Refresh failed'
                    : r.refreshStatus === 'succeeded'
                      ? r.refreshRequested
                        ? 'Refreshed just now'
                        : 'Showing cached transactions'
                      : r.refreshRequested || r.refreshStatus || r.lastAttemptedAt
                        ? 'Refresh status unknown'
                        : 'Never refreshed'
              }
              className={
                r.refreshStatus === 'succeeded' && r.refreshRequested
                  ? classes.chipPosted
                  : r.refreshStatus === 'failed'
                    ? classes.chipError
                    : r.refreshStatus === 'pending'
                      ? classes.chipPending
                      : classes.chipMuted
              }
            />
          )}
      </Box>

      {r.bestMatch && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.rows}>
            {renderRow('Bank transaction', formatDebit(r.bestMatch.amountCents), true)}
            {renderRow('Bank description', r.bestMatch.description || '-')}
            {renderRow('Bank status', statusChip(r.bestMatch.status))}
            {renderRow('Transacted', formatDateTime(r.bestMatch.transactedAt))}
            {renderRow('Posted', formatDateTime(r.bestMatch.postedAt))}
          </Box>
        </>
      )}

      {(r.availableBalanceCents !== null && r.availableBalanceCents !== undefined) ||
      (r.currentBalanceCents !== null && r.currentBalanceCents !== undefined) ? (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.rows}>
            {renderRow(
              'Balance',
              <Typography className={classes.verdictText} color="textSecondary">
                cached from the last balance check
                {r.balanceAsOf ? `, as of ${formatDateTime(r.balanceAsOf)}` : ''}. Use &quot;Check
                bank balance&quot; for a fresh one.
              </Typography>
            )}
            {renderRow(
              'Available balance',
              r.availableBalanceCents === null || r.availableBalanceCents === undefined
                ? 'n/a'
                : formatCentsPrice(r.availableBalanceCents)
            )}
            {renderRow(
              'Current balance',
              r.currentBalanceCents === null || r.currentBalanceCents === undefined
                ? 'n/a'
                : formatCentsPrice(r.currentBalanceCents)
            )}
          </Box>
        </>
      ) : null}

      {renderVerdict(r)}

      {renderCandidates(r.candidates || [], r.bestMatch?.id)}

      {renderOverride(r)}

      <Typography className={classes.checkedAt}>
        Checked at {formatDateTime(r.checkedAt)}
        {r.nextRefreshAvailableAt && new Date(r.nextRefreshAvailableAt).getTime() > Date.now()
          ? `, Stripe allows the next transactions refresh at ${formatDateTime(r.nextRefreshAvailableAt)}`
          : ''}
      </Typography>
    </>
  );

  return (
    <Dialog
      open={open}
      title="Bank transactions check"
      onCancel={handleClose}
      cancelText="Close"
      onConfirm={runCheck}
      confirmText="Check again"
      submitting={loading}
      error={error}
      note={`Read-only check through Stripe Financial Connections. When the debit is posted, use "Release pending funds" on this deposit; the release dialog picks up this result.${
        allocation && allocation.pendingAmountCents <= 0
          ? ' Nothing is pending on this deposit.'
          : ''
      }`}>
      {loading && !result ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : result ? (
        renderResult(result)
      ) : (
        <Typography color="textSecondary">No data</Typography>
      )}

      {requestOpen && (
        <RequestBankVerificationDialog
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          userId={userId}
          record={record}
          onRequested={() => {
            // the deposit row now carries the request; refreshing here would unmount this dialog, so the
            // datagrid is refreshed when this dialog closes, like after a check
            checkedRef.current = true;
            setRequested(true);
          }}
        />
      )}
    </Dialog>
  );
};

export default AchTransactionsCheckDialog;
